import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TrackingReportList from "./TrackingReportList";
import TrackingReportDetails from "./TrackingReportDetails";
import { Button, CircularProgress } from "@mui/material";
import { api, useGetTrackingReportsQuery } from "../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, debounce } from "lodash";
import { treeActions } from "../../../redux/slices/treeSlice";

const TrackingReports = ({ handleClose, open, setSnackbar, showCancelButton = false }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { permissions } = useSelector((state) => state.userPermissions);
	const [disableModification, setDisableModification] = useState(false);
	const [newTrackingStates, setNewTrackingStates] = useState({
		isOpen: false,
		copyFields: [],
		isOpenedByNewTR: false,
	});
	const [trackingReportList, setTrackingReportList] = useState([]);
	const {account , user} = useSelector(state => state.queryParams);
	const [enableApply, setEnableApply] = useState(false);
	const [selectedTR, setSelectedTR] = useState("");
	const listGridAPI = useRef();
	const detailsGridAPI = useRef();
	const [listLoading, setListLoading] = useState(false);
	const [applyLoading, setApplyLoading] = useState(false);

	const setListGridAPI = (api) => listGridAPI.current = api;
	const setDetailsGridAPI = (api) => detailsGridAPI.current = api;

	// Debounced snackbar function to prevent rapid successive error messages
	const debouncedSetSnackbar = useCallback(
		debounce((snackbarConfig) => {
			setSnackbar(snackbarConfig);
		}, 300),
		[setSnackbar]
	);

	const fetchTrackingReports = async (prioritySelectTR) => {
		setListLoading(true);
		try{
			const data = await dispatch(api.endpoints.getTrackingReports.initiate({ account, user }, {forceRefetch: true}))
			if (data.status === "fulfilled") {
			const trackingReports = data?.data?.tracking_reports || [];
			setTrackingReportList(trackingReports);
			if (prioritySelectTR) setSelectedTR(prioritySelectTR);
			else if (trackingReports?.length) setSelectedTR(trackingReports[0])
			}else {
				setTrackingReportList([]);		
				setSelectedTR("");
			}
			setListLoading(false);
		}catch(err) {
			setListLoading(false);
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		}
	}

	const applyHandler = (cb) => {
		if(!detailsGridAPI.current) return;
		setApplyLoading(true);
		const fields = []
		detailsGridAPI.current.forEachNode(node => {
			let nodeData = cloneDeep(node.data);
			if (nodeData?.hasOwnProperty("id")) delete nodeData?.id;
			if (nodeData?.hasOwnProperty("initialData")) delete nodeData?.initialData;
			if (nodeData.Field_Name) {
				const finalField = {
					"name": nodeData?.Field_Name,
					"data_type":nodeData?.Data_Type,
					"size": nodeData?.Size || (["varchar", "integer", "bigint"].includes(node?.Data_Type) ? "0" : ''),
					"set_insert":nodeData?.Is_Insert || "false",
					"set_update":nodeData?.Is_Update || "false",
					"set_key":nodeData?.Is_Key || "false"
				};
				fields.push(finalField);
			}
		})
		dispatch(api.endpoints.updateTrackingReport.initiate({
			data : {
				"type": selectedTR?.type?.toLowerCase() === "edi" ? "edi" : "other",
				"account_name":account,
				"user_name":user,
				"report_name":(selectedTR.name),
				"fields": fields
			}
		}, {forceRefetch: true}))
		.then((res => {
			if (res.data.current_state === "SUCCESS") {
				if (cb) cb()
				setEnableApply(false);
				setSnackbar({
					isOpen:true,
					severity:'success',
					message:res.data.message
				})
			}else {
				debouncedSetSnackbar({
					isOpen:true,
					severity:'error',
					message:res.data.message
				})
			}
		}))
		.catch((err) => {
			debouncedSetSnackbar({
				isOpen:true,
				severity:'error',
				message:err?.data?.message || t("BOX_MSG_GENERAL_ERROR")
			})
		})
		.finally(() => {
			setApplyLoading(false)
		})
	}
	
	const fieldsChangeHandler = useCallback((setIsChanged) => {
		if (!detailsGridAPI.current) return;
		let isChanged = setIsChanged;
		detailsGridAPI.current.forEachNode(node => {
			const initialData = node?.data?.initialData || {};
			const newData = node?.data || {};
			const initialDataKeys = Object.keys(initialData);
			if (!Object.keys(node?.data?.initialData || {}).length) {
				Object.keys(newData).forEach(e => {
					if(!["id", "initialData"].includes(e) && newData[e]) isChanged =true;
				})
			}else {
				initialDataKeys.forEach(key => {
					if (!["id", "initialData"].includes(key) && initialData[key] != newData[key]) {
						isChanged =true;
					}
				})
			}
			if (isChanged) {
				return;
			}
		})
		if (isChanged) setEnableApply(true);
		else setEnableApply(false);
	},[detailsGridAPI])

	const checkDuplicateNameTR =  (name) => {
		
	}

	useEffect(() => {
		if (open) fetchTrackingReports()
		else {
			setTrackingReportList([]);
			setSelectedTR("");
		}
		if (permissions.includes("TRACKING_REPORTS_RIGHTS_VIEW_ONLY")) setDisableModification(true);
		else setDisableModification(false);
	},[open])

	return (
		<div
			id='tracking-reports'
			style={{ overflow: "hidden", padding: "0 12px" }}
		>
			<div id='tracking-reports-desc'>{t("TRACKING_REPORT_DESC")}</div>
			<div id='tracking-reports-data'>
				<TrackingReportList
					disableModification={disableModification}
					setSnackbar={setSnackbar}
					setEnableApply={setEnableApply}
					applyLoading={applyLoading}
					isLoading={listLoading}
					newTrackingStates={newTrackingStates}
					setNewTrackingStates={setNewTrackingStates}
					trackingReportList={trackingReportList}
					setSelectedTR={setSelectedTR}
					selectedTR={selectedTR}
					fetchTrackingReports={fetchTrackingReports}
					setListGridAPI={setListGridAPI}
					enableApply={enableApply}
					detailsGridAPI={detailsGridAPI}
					applyHandler={applyHandler}
					listGridAPI={listGridAPI}
				/>
				<TrackingReportDetails
					setEnableApply={setEnableApply}
					detailsGridAPI={detailsGridAPI.current}
					disableModification={disableModification}
					setSnackbar={setSnackbar}
					fieldsChangeHandler={fieldsChangeHandler}
					setDetailsGridAPI={setDetailsGridAPI}
					selectedTR={selectedTR?.name || ""}
					trackingReportList={trackingReportList}
					TRLoading={listLoading}
					
				/>
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					gap: "10px",
				}}
			>
				<div></div>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						gap: "10px",
					}}
				>
					{disableModification ? "" : <Button
						disabled={applyLoading}
						variant='outlined'
						onClick={() => setNewTrackingStates({
							copyFields: [],
							isOpen: true,
							isOpenedByNewTR: true
						})}
					>
						{t("BTN_NEW_TRACKING_REPORT")}
					</Button>}
					{disableModification? "" : <Button
						disabled={!enableApply}
						variant='outlined'
						onClick={() => applyHandler(() => {
							fetchTrackingReports(selectedTR);
							dispatch(treeActions?.setTrackingReportUpdated());
						})}
					>
						{applyLoading ? <CircularProgress size={"18px"}/> : t("APPLY")}
					</Button>}
					{showCancelButton && (
						<Button variant='outlined' onClick={() => handleClose(false)}>
							{t("CANCEL")}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default TrackingReports;
